'use client';

import { createClient } from './client';

export interface Category {
  id: string;
  name: string;
  icon: string;
  description: string;
  color: string;
  isActive: boolean;
  articlesCount: number;
  createdAt: string;
  updatedAt: string;
}

function isSchemaError(error: any): boolean {
  if (!error) return false;
  
  if (error.code && typeof error.code === 'string') {
    const errorClass = error.code.substring(0, 2);
    if (errorClass === '42' || errorClass === '08') {
      return true;
    }
    if (errorClass === '23') {
      return false;
    }
  }
  
  if (error.message) {
    const schemaErrorPatterns = [
      /relation.*does not exist/i,
      /column.*does not exist/i,
      /function.*does not exist/i,
      /syntax error/i,
    ];
    return schemaErrorPatterns.some(pattern => pattern.test(error.message));
  }
  
  return false;
}

function convertToCategory(row: any): Category {
  return {
    id: row.id,
    name: row.name,
    icon: row.icon,
    description: row.description,
    color: row.color,
    isActive: row.is_active,
    articlesCount: row.articles_count,
    createdAt: row.created_at,
    updatedAt: row.updated_at,
  };
}

export const categoryService = {
  async getAll(): Promise<Category[]> {
    try {
      const supabase = createClient();
      if (!supabase) return [];

      const { data, error } = await supabase
        .from('news_categories')
        .select('*')
        .order('name');

      if (error) {
        if (isSchemaError(error)) {
          console.error('Schema error:', error.message);
          throw error;
        } else {
          console.log('Data error:', error.message);
          return [];
        }
      }

      return data?.map(convertToCategory) || [];
    } catch (error: any) {
      console.log('Schema-related error:', error.message);
      throw error;
    }
  },

  async create(category: Omit<Category, 'id' | 'createdAt' | 'updatedAt' | 'articlesCount'>): Promise<{ success: boolean; data?: Category; error?: string }> {
    try {
      const supabase = createClient();
      if (!supabase) return { success: false, error: 'Supabase client not initialized' };

      const { data, error } = await supabase
        .from('news_categories')
        .insert({
          name: category.name,
          icon: category.icon,
          description: category.description,
          color: category.color,
          is_active: category.isActive,
        })
        .select()
        .single();

      if (error) {
        if (isSchemaError(error)) {
          console.error('Schema error:', error.message);
          throw error;
        } else {
          console.log('Data error:', error.message);
          return { success: false, error: error.message };
        }
      }

      return { success: true, data: convertToCategory(data) };
    } catch (error: any) {
      console.log('Schema-related error:', error.message);
      throw error;
    }
  },

  async update(id: string, category: Partial<Omit<Category, 'id' | 'createdAt' | 'updatedAt' | 'articlesCount'>>): Promise<{ success: boolean; data?: Category; error?: string }> {
    try {
      const supabase = createClient();
      if (!supabase) return { success: false, error: 'Supabase client not initialized' };

      const updateData: any = {};
      if (category.name !== undefined) updateData.name = category.name;
      if (category.icon !== undefined) updateData.icon = category.icon;
      if (category.description !== undefined) updateData.description = category.description;
      if (category.color !== undefined) updateData.color = category.color;
      if (category.isActive !== undefined) updateData.is_active = category.isActive;

      const { data, error } = await supabase
        .from('news_categories')
        .update(updateData)
        .eq('id', id)
        .select()
        .single();

      if (error) {
        if (isSchemaError(error)) {
          console.error('Schema error:', error.message);
          throw error;
        } else {
          console.log('Data error:', error.message);
          return { success: false, error: error.message };
        }
      }

      return { success: true, data: convertToCategory(data) };
    } catch (error: any) {
      console.log('Schema-related error:', error.message);
      throw error;
    }
  },

  async delete(id: string): Promise<{ success: boolean; error?: string }> {
    try {
      const supabase = createClient();
      if (!supabase) return { success: false, error: 'Supabase client not initialized' };

      const { error } = await supabase
        .from('news_categories')
        .delete()
        .eq('id', id);

      if (error) {
        if (isSchemaError(error)) {
          console.error('Schema error:', error.message);
          throw error;
        } else {
          console.log('Data error:', error.message);
          return { success: false, error: error.message };
        }
      }

      return { success: true };
    } catch (error: any) {
      console.log('Schema-related error:', error.message);
      throw error;
    }
  },
};